/**
 * Display helpers shared across pages. Match the Python CLI's formatting
 * (`mttr.__main__.fmt_duration`) so operators see the same time strings.
 */

/** Render seconds as '3d 4h' / '2h 15m' / '45s' / '-' for null. */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "-";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (hours < 24) return `${hours}h ${m}m`;
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return `${days}d ${h}h`;
}

// Display timezone (IANA, e.g. "America/Chicago"). Set from the logged-in
// user's company `display_timezone` (AuthContext.setDisplayTimezone); null =>
// render in UTC, preserving the original behaviour.
let _displayTz: string | null = null;

/** Set the timezone all absolute timestamps render in. null/empty => UTC. */
export function setDisplayTimezone(tz: string | null | undefined): void {
  _displayTz = tz && tz.trim() ? tz.trim() : null;
}

/** The active display timezone, or null when rendering in UTC. */
export function getDisplayTimezone(): string | null {
  return _displayTz;
}

function _renderTs(d: Date, tz: string): string {
  // Compact display: "Jun 10, '26 8:13 CDT". Hour has no leading zero
  // ("8:13", not "08:13") per Jason 2026-06-10; minute stays
  // 2-digit so the colon-separated pair reads as a time.
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "short",
      day: "numeric",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    }).formatToParts(d);
    const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
    const zone = get("timeZoneName");
    // Some locales render midnight hour as "24"; normalise to 0. Also
    // strip any incidental leading zero on the hour part.
    const hh = String(parseInt(get("hour"), 10) % 24);
    return `${get("month")} ${get("day")}, '${get("year")} ${hh}:${get("minute")}${
      zone ? ` ${zone}` : ""
    }`;
  } catch {
    // Invalid timezone string → fall back to manual UTC formatting in
    // the same short style.
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const yy = String(d.getUTCFullYear() % 100).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, '${yy} ${d.getUTCHours()}:${mm} UTC`;
  }
}

/** ISO-8601 string → "Jun 10, '26 8:13 CDT" in the configured display
 *  timezone (UTC when none is set). Compact format per Jason
 *  2026-06-10 — shorter than the previous "YYYY-MM-DD HH:MM ZZZ" so
 *  it fits in narrow columns (Departures, OutagesPage). The trailing
 *  zone abbreviation keeps it unambiguous. */
export function fmtUtc(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // pass through malformed
  return _renderTs(d, _displayTz ?? "UTC");
}

/** Same as `fmtUtc` but renders in an explicit IANA timezone instead of the
 *  global display-tz. Used when a single page wants to render each row in
 *  the box's LOCAL time (e.g. Departures, where one customer's fleet spans
 *  Central/Mountain). Pass null → falls back to the global display tz. */
export function fmtUtcInTz(
  iso: string | null | undefined,
  tz: string | null | undefined,
): string {
  if (!tz) return fmtUtc(iso);
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return _renderTs(d, tz);
}

/** Heuristic IANA-tz from lat/lon for the US fleet. Defaults to
 *  America/Chicago — most of the fleet is in TX/OK/ND/NM (Central).
 *  Boxes whose Peplink GPS lands in the NYC metro (a known erroneous
 *  fix some Peplinks emit when they can't resolve a position) are also
 *  pinned to Central per Jason 2026-06-10. */
export function tzFromLatLon(
  lat: number | null | undefined,
  lon: number | null | undefined,
): string {
  if (lat == null || lon == null) return "America/Chicago";
  if (lat >= 40 && lat <= 41.2 && lon >= -74.5 && lon <= -73.5) {
    return "America/Chicago";
  }
  if (lon <= -114) return "America/Los_Angeles";
  if (lon <= -97)  return "America/Denver";
  if (lon <= -82)  return "America/Chicago";
  return "America/New_York";
}

/** Houston HQ coordinates (7954 Breen Dr, Houston TX 77064). NYC-metro
 *  Peplink fixes are remapped here on the map per Jason 2026-06-10 (known
 *  bogus fix). Coords match mttr/map_export.py:_HQ_DEFAULT_LAT/LON so the
 *  fallback anchor and the NYC-remap point land on the same pixel.
 *  Address moved from 6602 Petropark Dr on 2026-07-17 to match the rest
 *  of the codebase (was left behind by commit eb88baf). */
const HOUSTON_HQ_LAT = 29.899188;
const HOUSTON_HQ_LON = -95.507188;

/** Remap a Peplink GPS coord that lands in the NYC metro to Houston HQ
 *  for map rendering. All other coords pass through unchanged. Mirrors
 *  the tz remap in `tzFromLatLon`. Returns a fresh mutable tuple so
 *  leaflet's `LatLngTuple` (mutable) accepts it without a cast. */
export function mapDisplayCoords(
  lat: number,
  lon: number,
): [number, number] {
  if (lat >= 40 && lat <= 41.2 && lon >= -74.5 && lon <= -73.5) {
    return [HOUSTON_HQ_LAT, HOUSTON_HQ_LON];
  }
  return [lat, lon];
}

/** Strip up to two LEADING zeros from each token of a workcenter
 *  name. A "leading" zero is one at string-start or immediately
 *  after a non-word char (space, dash, slash, etc.). Inner zeros are
 *  preserved — "00X07" -> "X07" (rig name), NOT "X7".
 *
 *  Per Jason 2026-06-10: humans say "X07", "Bay 3", "X41 LA" — the
 *  "00" prefix on workcenter names in md_workcenter is just storage
 *  padding, not part of the display name. At most 2 leading zeros
 *  per token (real data only ever has 0-2), so the rule caps there
 *  to avoid eating real zeros from weird future inputs.
 *
 *  Examples:
 *    "00X07"             -> "X07"
 *    "00X28"             -> "X28"
 *    "001"               -> "1"
 *    "01201"             -> "1201"
 *    "RIG 00X41 LA"      -> "RIG X41 LA"
 *    "Bay 003"           -> "Bay 3"
 *    "X07"               -> "X07"   (no leading zeros — preserved)
 *    "X24" / "1208"      -> as-is
 *    "100"               -> "100"   (zero is inner)
 *    null/""             -> ""
 */
export function fmtWorkCenter(name: string | null | undefined): string {
  if (!name) return "";
  return name.replace(/(^|\W)0{1,2}(?=\w)/g, "$1");
}

/** Convenience: customer/work_center display, dash for both null.
 *  Workcenter is normalised through fmtWorkCenter so leading-zero
 *  rigs render compact ("X7" not "X07"). */
export function fmtCustomerWc(
  customer: string | null,
  workCenter: string | null,
): string {
  const parts = [customer, fmtWorkCenter(workCenter) || null].filter(Boolean);
  return parts.length > 0 ? parts.join(" / ") : "-";
}

/** Abbreviate a full SCU name (e.g. "SCU-PI4T2J8L-GEN5") to its
 *  middle device-id segment ("PI4T2J8L") for compact UI display. The
 *  full form stays in the data; this is purely a render-side trim.
 *  Names that don't match the SCU-{id}-GEN5 pattern pass through. */
export function shortScuName(scu: string | null | undefined): string {
  if (!scu) return "";
  const m = /^SCU-(.+?)-GEN5$/.exec(scu);
  return m ? m[1] : scu;
}

/** Extract the "SB###" smartbox number from the leading segment of a
 *  starlink_name like "SB019 - ND Yard - rpi-4T2J8L - Standard".
 *  Returns null for names that don't match the pattern (or are null). */
export function parseSmartboxNumber(
  starlinkName: string | null | undefined,
): string | null {
  if (!starlinkName) return null;
  const m = /^SB\d+/.exec(starlinkName);
  return m ? m[0] : null;
}

/** ISO timestamp → "5m" / "2h" / "3d" relative-to-now. Empty string for
 *  null so callers can render "Pep ● online {fmtAgo(t)}" without leftover
 *  whitespace when the timestamp is missing. */
export function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const seconds = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  return fmtDuration(seconds);
}


/** Money, formatted for display.
 *
 *  There is deliberately no "masked" variant. A sanitised session receives
 *  STAND-IN figures from the server rather than blanks (see
 *  mttr/app_api/masking.py), so every price here formats the same way whoever
 *  is looking -- the numbers are simply not the real ones. The "these are
 *  stand-ins" caveat is carried once, by the banner, not repeated per cell. */
export function fmtMoney(
  value: number | null | undefined,
  currency?: string | null,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value == null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
    ...opts,
  }).format(value);
}
